/** BFF logging program for Consumer PWA (WI 37464). Sent as the `program` body key. */
export const PROGRAM_NAME = 'jiocloudpc_pwa';

/**
 * `logType` — which family of log a `POST api/log/user` body belongs to. Mandatory on
 * every call; the BFF partitions on it.
 *
 * Note the camelCase key: the rest of this payload is snake_case, but the backend
 * defined this one as `logType`, so it is sent verbatim.
 */
export const AnalyticsLogTypes = {
  /** Every user-interaction event — the default for `AnalyticsService.log()`. */
  CLICK_EVENT: 'click_event',
} as const;

export type AnalyticsLogType = (typeof AnalyticsLogTypes)[keyof typeof AnalyticsLogTypes];

/**
 * `usecase_identification` — optional marker for the *first-time-user* journey.
 *
 * Its only job is to distinguish a first-time user from a returning one, so it is
 * set for the three sign-up journeys and omitted entirely for a returning user's
 * plain login. Set once via `AnalyticsService.setUsecase()` when the journey is
 * identified; later log events *and* the auth API requests then carry it.
 */
export const AnalyticsUsecases = {
  /** Arrived with a plan already bought on jio.com, single subscriber. */
  SETUP_PAGE: 'setup_page',
  /** No plan yet — routed to the recharge screen and on to jio.com. */
  NEW_ACCOUNT_PAGE: 'new_account_page',
  /** Plan bought, but the phone/FLN resolves to more than one subscriber_id. */
  MULTI_ACCOUNT_PAGE: 'multi_account_page',
} as const;

export type AnalyticsUsecase = (typeof AnalyticsUsecases)[keyof typeof AnalyticsUsecases];

/**
 * `page_title` values the client emits.
 *
 * The first block is the pre-login/journey set. Everything from `LOGIN_PAGE` down is
 * the post-login set (spec `logs_for_jiopc_pwa_005.json`): the four entry journeys all
 * converge on the same dashboard/profile code, so these are declared and instrumented
 * **once**, not once per entry flow.
 *
 * Note on `LOGIN_PAGE`: the `page_load` for the login/home/profile screens is still
 * raised backend-side, as it always was. `LOGIN_PAGE` is declared here only because the
 * post-login spec asks the *client* to raise the `forgot_password` event against it —
 * it is not a client-side `page_load`.
 *
 * Entries marked "not emitted yet" are declared deliberately: the spec defines them but
 * the feature they describe doesn't exist in the app yet. See
 * `docs/consumer/logs/post_login_logs_implementation_plan.md` for what each is blocked on.
 */
export const AnalyticsPages = {
  /**
   * Browser hit the JioPC URL and stopped on the install gate, before signup.
   * Production blocks browser access, so this is the visit that never starts
   * registration. Distinct from `signup_page`. BE-allowlisted on `api/log/user`
   * for guest `device_id` visits (see {@link AcceptedGuestAnalyticsPages}).
   */
  WEBSITE_PAGE: 'website_page',
  /** Whole phone → account → set-password → OTP journey. */
  SIGNUP_PAGE: 'signup_page',
  /**
   * Pre-login only: the screen a user with no assigned plan is routed to in order to
   * recharge and continue logging in. Not to be confused with the post-login renew
   * CTA, which is `renew_plan` on `home_page` / `subscription_page`.
   */
  RECHARGE_PAGE: 'recharge_page',
  /** Dashboard — the landing that closes every journey. */
  HOME_PAGE: 'home_page',

  // ── Post-login ──
  LOGIN_PAGE: 'login_page',
  /** The feedback / help / product-tour card strip on the dashboard. */
  HOME_PAGE_CARD: 'home_page_card',
  /**
   * The renew/recharge QR dialog raised from the dashboard, and the Renew CTA on the
   * pre-deletion dialog. Both used to report as `recharge_page`, which is pre-login only
   * (see its comment above), so post-login renew traffic was indistinguishable from a
   * no-plan-assigned login. Split out so each surface is queryable on its own.
   */
  RECHARGE_QR_POPUP: 'recharge_qr_popup',
  PRE_DELETION_POPUP: 'pre_deletion_popup',
  HOME_PAGE_POST_DATA_DELETION_POPUP: 'home_page_post_data_deletion_popup',
  /** Not emitted yet — no plan-about-to-expire popup exists; expiry is a banner. */
  HOME_PAGE_PLAN_ABOUT_TO_EXPIRE_POPUP: 'home_page_plan_about_to_expire_popup',
  PROFILE_SETTINGS_PAGE: 'profile_settings_page',
  MANAGE_PROFILES_PAGE: 'manage_profiles_page',
  EDIT_PROFILE_DETAILS_PAGE: 'edit_profile_details_page',
  CHANGE_AVATAR_PAGE: 'change_avatar_page',
  /** Not emitted yet — "verify mobile" is a coming-soon toast, there is no OTP screen. */
  VERIFY_NUMBER_OTP_PAGE: 'verify_number_otp_page',
  EDIT_PROFILE_UPDATE_PASSWORD_PAGE: 'edit_profile_update_password_page',
  EDIT_PROFILE_UPDATE_PASSWORD_OTP_PAGE: 'edit_profile_update_password_otp_page',
  VERIFY_EMAIL_OTP_PAGE: 'verify_email_otp_page',
  SECURITY_PIN_PAGE: 'security_pin_page',
  SECURITY_PIN_SETUP_PAGE: 'security_pin_setup_page',
  /**
   * Spec writes this as `disable_pin_password_popup` on `continue` but
   * `disable_pin_password_pop_up_page` on `go_back`; normalized to the former here
   * (raised with the spec owner).
   */
  DISABLE_PIN_PASSWORD_POPUP: 'disable_pin_password_popup',
  CHANGE_PIN_PASSWORD_POPUP: 'change_pin_password_popup',
  CHANGE_SECURITY_PIN_PAGE: 'change_security_pin_page',
  SUBSCRIPTION_PAGE: 'subscription_page',
  LOGOUT_POPUP: 'logout_popup',
  PWA_EXIT_POPUP: 'pwa_exit_popup',
  /**
   * The post-VDI-session feedback form. Only `skip` is client-raised — the `submit` log is
   * raised backend-side off the `page` field the submit API already carries ('feedback_page').
   */
  FEEDBACK_PAGE: 'feedback_page',
  /** Default page_title for VDI click events emitted from the HyLite iframe session. */
  VDI_SESSION: 'vdi_session',
} as const;

export type AnalyticsPage = (typeof AnalyticsPages)[keyof typeof AnalyticsPages];

/**
 * Guest / pre-login `page_title` values `api/log/user` accepts today.
 * {@link AnalyticsPages} is the full client catalog; this set is the BE allowlist
 * we emit against for anonymous/`device_id` visits.
 */
export const AcceptedGuestAnalyticsPages = {
  WEBSITE_PAGE: AnalyticsPages.WEBSITE_PAGE,
  SIGNUP_PAGE: AnalyticsPages.SIGNUP_PAGE,
  RECHARGE_PAGE: AnalyticsPages.RECHARGE_PAGE,
} as const;

export type AcceptedGuestAnalyticsPage =
  (typeof AcceptedGuestAnalyticsPages)[keyof typeof AcceptedGuestAnalyticsPages];

const ACCEPTED_GUEST_ANALYTICS_PAGE_TITLES: ReadonlySet<AnalyticsPage> = new Set(
  Object.values(AcceptedGuestAnalyticsPages),
);

/** True when `page` is on the live guest allowlist for `api/log/user`. */
export function isAcceptedGuestAnalyticsPage(page: AnalyticsPage): boolean {
  return ACCEPTED_GUEST_ANALYTICS_PAGE_TITLES.has(page);
}

/**
 * Use `preferred` when BE accepts it; otherwise `fallback` (default `signup_page`).
 * Keeps guest visit logs from 401-ing on titles the allowlist does not yet include.
 */
export function resolveAcceptedGuestAnalyticsPage(
  preferred: AnalyticsPage,
  fallback: AnalyticsPage = AnalyticsPages.SIGNUP_PAGE,
): AnalyticsPage {
  return isAcceptedGuestAnalyticsPage(preferred) ? preferred : fallback;
}

/** Install-gate visit / CTA page_title (`website_page` once BE-allowlisted). */
export const INSTALL_GATE_PAGE_TITLE = resolveAcceptedGuestAnalyticsPage(
  AnalyticsPages.WEBSITE_PAGE,
);

/**
 * `event` values the client emits. In the *pre-login* journeys most events
 * (password_entered, otp_submit/otp_resend, …) are raised by the backend and so are
 * deliberately absent; the post-login block below is client-raised throughout.
 *
 * Several of these are alternate values of the same `event` field on one page (e.g.
 * `feedback` / `help` / `product_tour` on `home_page_card`) rather than distinct
 * fields — see the per-screen table in
 * `docs/consumer/logs/post_login_logs_implementation_plan.md`.
 */
export const AnalyticsEvents = {
  PAGE_LOAD: 'page_load',
  GO_BACK: 'go_back',
  PROCEED: 'proceed',
  ACCOUNT_SELECTED: 'account_selected',
  /** Install-gate CTA — "Install JioPC" / install-now on `website_page`. */
  INSTALL_NOW: 'install_now',
  /** Install-gate CTA — "Launch app" / launch-now on `website_page`. */
  LAUNCH_NOW: 'launch_now',

  // ── Post-login ──
  FORGOT_PASSWORD: 'forgot_password',
  PROFILE_ICON: 'profile_icon',
  LAUNCH_JIOCLOUD_PC: 'launch_jiocloud_pc',
  /** Not emitted yet — no password-sync flow exists in the app. */
  PASSWORD_SYNC_STATUS: 'password_sync_status',
  /**
   * No longer emitted — dropped from the client-raised event set by product decision. Kept in
   * this table (like PASSWORD_SYNC_STATUS above) because the table mirrors the analytics spec,
   * so a spec name with no live call site is documented rather than deleted. The
   * provisioning-status API call still runs as the VDI launch's first gate; only the event went.
   */
  PROVISION_STATUS: 'provision_status',
  /** Post-login renew CTA. Distinct from the pre-login `recharge_page` flow. */
  RENEW_PLAN: 'renew_plan',
  FEEDBACK: 'feedback',
  HELP: 'help',
  PRODUCT_TOUR: 'product_tour',
  SUBSCRIPTIONS: 'subscriptions',
  MANAGE_PROFILES: 'manage_profiles',
  PROFILE_DETAILS: 'profile_details',
  SECURITY_PIN: 'security_pin',
  CHANGE_AVATAR: 'change_avatar',
  VERIFY_PHONE_NUMBER: 'verify_phone_number',
  CHANGE_PASSWORD: 'change_password',
  SAVE_CHANGES: 'save_changes',
  VERIFY_EMAIL: 'verify_email',
  AVATAR_SELECTED: 'avatar_selected',
  CANCEL: 'cancel',
  CONFIRM: 'confirm',
  RESEND_OTP: 'resend_otp',
  ENABLE_PIN: 'enable_pin',
  DISABLE_PIN: 'disable_pin',
  SET_PIN: 'set_pin',
  CONTINUE: 'continue',
  /**
   * Dismissing the post-VDI-session feedback form. Its counterpart `submit` is not here: that
   * one is raised backend-side, so declaring it would imply a client call site that does not
   * exist.
   */
  SKIP: 'skip',
  /** Not emitted yet — CTAs on the plan-about-to-expire popup, which doesn't exist. */
  VIEW_PLANS: 'view_plans',
  RENEW_PLANS: 'renew_plans',
  LOGOUT: 'logout',
  DISCONNECT: 'disconnect',
  PWA_EXIT: 'pwa_exit',
} as const;

export type AnalyticsEvent = (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents];
